import { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Chip, TextField, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, IconButton, Tooltip, TablePagination,
} from '@mui/material';
import { Add, Edit, AddCircle, RemoveCircle } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { farmInputService } from '@smartgrow/shared';
import type { FarmInput, CreateFarmInputRequest, InputType, FarmInputUsageLogRequest, FarmInputRestockLogRequest } from '@smartgrow/shared';

const INPUT_TYPES: InputType[] = ['SEED', 'FERTILIZER', 'AGROCHEMICAL', 'FEED', 'FUEL', 'WATER', 'OTHER'];
const fmt = (n?: number) => n != null ? `₦${n.toLocaleString('en-NG')}` : '—';

const emptyForm = (): CreateFarmInputRequest => ({
  name: '',
  inputType: 'SEED',
  unit: '',
  quantityInStock: 0,
  reorderLevel: undefined,
  costPerUnit: undefined,
  supplierName: '',
  location: '',
  notes: '',
});

export const FarmInputsTab = () => {
  const qc = useQueryClient();

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateFarmInputRequest>(emptyForm());
  const [usageDialog, setUsageDialog] = useState<FarmInput | null>(null);
  const [restockDialog, setRestockDialog] = useState<FarmInput | null>(null);
  const [usageForm, setUsageForm] = useState<FarmInputUsageLogRequest>({ quantityUsed: 0, usageDate: new Date().toISOString().split('T')[0] });
  const [restockForm, setRestockForm] = useState<FarmInputRestockLogRequest>({ quantityAdded: 0, restockDate: new Date().toISOString().split('T')[0] });
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['farmInputs', typeFilter, page, debouncedSearch],
    queryFn: () => farmInputService.search({
      page, size: 10,
      search: debouncedSearch || undefined,
      type: typeFilter === 'ALL' ? undefined : typeFilter,
    }),
  });

  const inputs: FarmInput[] = data?.content ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['farmInputs'] });

  const saveMutation = useMutation({
    mutationFn: () => editingId ? farmInputService.update(editingId, form) : farmInputService.create(form),
    onSuccess: () => { invalidate(); setFormOpen(false); setEditingId(null); setForm(emptyForm()); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Save failed'),
  });

  const usageMutation = useMutation({
    mutationFn: () => farmInputService.recordUsage(usageDialog!.id, usageForm),
    onSuccess: () => { invalidate(); setUsageDialog(null); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Usage record failed'),
  });

  const restockMutation = useMutation({
    mutationFn: () => farmInputService.recordRestock(restockDialog!.id, restockForm),
    onSuccess: () => { invalidate(); setRestockDialog(null); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Restock record failed'),
  });

  const openEdit = (input: FarmInput) => {
    setEditingId(input.id);
    setForm({
      name: input.name,
      inputType: input.inputType,
      unit: input.unit ?? '',
      quantityInStock: input.quantityInStock,
      reorderLevel: input.reorderLevel,
      costPerUnit: input.costPerUnit,
      supplierName: input.supplierName ?? '',
      location: input.location ?? '',
      notes: input.notes ?? '',
    });
    setFormOpen(true);
  };

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search inputs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          select
          size="small"
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="ALL">All Types</MenuItem>
          {INPUT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingId(null); setForm(emptyForm()); setFormOpen(true); }}>
          Add Input
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>In Stock</TableCell>
                <TableCell>Reorder At</TableCell>
                <TableCell>Cost/Unit</TableCell>
                <TableCell>Stock Value</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inputs.map((input: FarmInput, i: number) => (
                <TableRow key={input.id} hover>
                  <TableCell>{page * 10 + i + 1}</TableCell>
                  <TableCell><Typography variant="body2" fontWeight="600">{input.name}</Typography></TableCell>
                  <TableCell><Chip label={input.inputTypeLabel || input.inputType} size="small" /></TableCell>
                  <TableCell>
                    <Typography variant="body2" color={input.lowStock ? 'warning.main' : 'inherit'} fontWeight={input.lowStock ? 700 : 400}>
                      {input.quantityInStock.toLocaleString()} {input.unit ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell>{input.reorderLevel != null ? `${input.reorderLevel} ${input.unit ?? ''}` : '—'}</TableCell>
                  <TableCell>{fmt(input.costPerUnit)}</TableCell>
                  <TableCell>{fmt(input.totalStockValue)}</TableCell>
                  <TableCell>{input.supplierName ?? '—'}</TableCell>
                  <TableCell>
                    {input.expired && <Chip label="Expired" color="error" size="small" sx={{ mr: 0.5 }} />}
                    {input.lowStock && <Chip label="Low Stock" color="warning" size="small" />}
                    {!input.expired && !input.lowStock && <Chip label="OK" color="success" size="small" />}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(input)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Record Usage">
                      <IconButton size="small" color="warning" onClick={() => { setUsageDialog(input); setUsageForm({ quantityUsed: 0, usageDate: new Date().toISOString().split('T')[0] }); }}>
                        <RemoveCircle fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Restock">
                      <IconButton size="small" color="success" onClick={() => { setRestockDialog(input); setRestockForm({ quantityAdded: 0, restockDate: new Date().toISOString().split('T')[0] }); }}>
                        <AddCircle fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {inputs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="text.secondary" py={3}>No farm inputs found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data?.totalElements ?? 0}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Farm Input' : 'Add Farm Input'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth size="small" />
          <TextField select label="Type *" value={form.inputType} onChange={(e) => setForm({ ...form, inputType: e.target.value as InputType })} fullWidth size="small">
            {INPUT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField label="Unit (kg, liters...)" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} fullWidth size="small" />
          <TextField label="Quantity in Stock" type="number" value={form.quantityInStock ?? ''} onChange={(e) => setForm({ ...form, quantityInStock: parseFloat(e.target.value) || 0 })} fullWidth size="small" />
          <TextField label="Reorder Level" type="number" value={form.reorderLevel ?? ''} onChange={(e) => setForm({ ...form, reorderLevel: parseFloat(e.target.value) || undefined })} fullWidth size="small" />
          <TextField label="Cost per Unit (₦)" type="number" value={form.costPerUnit ?? ''} onChange={(e) => setForm({ ...form, costPerUnit: parseFloat(e.target.value) || undefined })} fullWidth size="small" />
          <TextField label="Supplier Name" value={form.supplierName} onChange={(e) => setForm({ ...form, supplierName: e.target.value })} fullWidth size="small" />
          <TextField label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} fullWidth size="small" />
          <TextField label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth size="small" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.name.trim()}>
            {editingId ? 'Save Changes' : 'Add Input'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usage Dialog */}
      <Dialog open={!!usageDialog} onClose={() => setUsageDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Usage — {usageDialog?.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {usageDialog && (
            <Typography variant="body2" color="primary.main" fontWeight={700}>
              Current stock: {usageDialog.quantityInStock.toLocaleString()} {usageDialog.unit ?? ''}
            </Typography>
          )}
          <TextField label={`Quantity Used${usageDialog?.unit ? ` (${usageDialog.unit})` : ''} *`} type="number" value={usageForm.quantityUsed} onChange={(e) => setUsageForm({ ...usageForm, quantityUsed: parseFloat(e.target.value) || 0 })} fullWidth size="small" />
          <TextField label="Usage Date *" type="date" value={usageForm.usageDate} onChange={(e) => setUsageForm({ ...usageForm, usageDate: e.target.value })} fullWidth size="small" InputLabelProps={{ shrink: true }} />
          <TextField label="Recorded By" value={usageForm.recordedBy ?? ''} onChange={(e) => setUsageForm({ ...usageForm, recordedBy: e.target.value })} fullWidth size="small" />
          <TextField label="Notes" value={usageForm.notes ?? ''} onChange={(e) => setUsageForm({ ...usageForm, notes: e.target.value })} fullWidth size="small" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageDialog(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => usageMutation.mutate()} disabled={usageMutation.isPending || usageForm.quantityUsed <= 0}>
            Record Usage
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restock Dialog */}
      <Dialog open={!!restockDialog} onClose={() => setRestockDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Restock — {restockDialog?.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {restockDialog && (
            <Typography variant="body2" color="success.main" fontWeight={700}>
              Current stock: {restockDialog.quantityInStock.toLocaleString()} {restockDialog.unit ?? ''}
            </Typography>
          )}
          <TextField label={`Quantity Added${restockDialog?.unit ? ` (${restockDialog.unit})` : ''} *`} type="number" value={restockForm.quantityAdded} onChange={(e) => setRestockForm({ ...restockForm, quantityAdded: parseFloat(e.target.value) || 0 })} fullWidth size="small" />
          <TextField label="Cost per Unit (₦)" type="number" value={restockForm.costPerUnit ?? ''} onChange={(e) => setRestockForm({ ...restockForm, costPerUnit: parseFloat(e.target.value) || undefined })} fullWidth size="small" />
          <TextField label="Supplier Name" value={restockForm.supplierName ?? ''} onChange={(e) => setRestockForm({ ...restockForm, supplierName: e.target.value })} fullWidth size="small" />
          <TextField label="Restock Date *" type="date" value={restockForm.restockDate} onChange={(e) => setRestockForm({ ...restockForm, restockDate: e.target.value })} fullWidth size="small" InputLabelProps={{ shrink: true }} />
          <TextField label="Invoice Reference" value={restockForm.invoiceReference ?? ''} onChange={(e) => setRestockForm({ ...restockForm, invoiceReference: e.target.value })} fullWidth size="small" />
          <TextField label="Notes" value={restockForm.notes ?? ''} onChange={(e) => setRestockForm({ ...restockForm, notes: e.target.value })} fullWidth size="small" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestockDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => restockMutation.mutate()} disabled={restockMutation.isPending || restockForm.quantityAdded <= 0}>
            Record Restock
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
